import {Injectable} from '@angular/core';
import {AppConfigService} from './app-config.service';
import {BsToastService} from './bs-toast-service';

/**
 * NotificationService is responsible for displaying notifications to the user.
 * It serves as a transitional service between the legacy toastr notification system
 * and the new bsToastService. It configures and displays different types of notification
 * messages such as success, error, warning, and info.
 * This service will be deprecated in the future once the migration to bsToastService is complete.
 *
 * Dependencies:
 * - ToastrService: The legacy toastr notification service which should be removed after msgs move to this service.
 * - AppConfigService: Provides configuration values such as timeOut for notifications.
 * - BsToastService: The new notification service to handle toast messages.
 *
 * Features:
 * - Supports multiple notification types (success, error, warning, info).
 * - Provides configurable options such as timeout, position, and close button visibility.
 * - Allows certain message types to stay on screen indefinitely by adjusting configuration.
 *
 * Public Methods:
 * - success(message): Displays a success notification.
 * - bsSuccess(message: string): Direct call to BsToastService for success notifications.
 * - error(message): Displays an error notification with optional filtering for 'UNDEFINED' messages.
 * - bsError(message: string): Direct call to BsToastService for error notifications.
 * - errorStayOnScreen(message): Displays an error notification that remains on screen indefinitely.
 * - warning(message): Displays a warning notification.
 * - bsWarning(message: string): Direct call to BsToastService for warning notifications.
 * - warningStayOnScreen(message): Displays a warning notification that remains on screen indefinitely.
 * - info(message): Displays an informational notification.
 * - bsInfo(message: string): Direct call to BsToastService for informational notifications.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  public configuration: any = {
    timeOut: 1000,
    positionClass: 'toast-top-right',
    enableHtml: true,
    extendedTimeOut: 0,
    closeButton: false,
    closeTime: 1000
  };

  timeout: number;
  close: number;

  constructor(
    public appConfigService: AppConfigService,
    public bsToast: BsToastService) {
    // Guard against AppConfig not yet loaded during early app initialization (APP_INITIALIZER race)
    // Fallback to sane defaults and attempt to read real values lazily once available.
    try {
      this.timeout = this.appConfigService.toastrWarnTimeOut;
      this.close = this.appConfigService.toastrWarnTimeOut;
    } catch {
      // Use defaults until AppConfigService finishes loading
      this.timeout = 3000;
      this.close = 3000;
      // Best-effort: schedule a microtask to re-read after app config likely loaded
      Promise.resolve().then(() => {
        try {
          this.timeout = this.appConfigService.toastrWarnTimeOut;
          this.close = this.appConfigService.toastrWarnTimeOut;
        } catch {
          // keep defaults if still not ready
        }
      });
    }
  }

  // TODO NOTES - this service can be removed at some point when old toastr is assured removed.
  //  It is being used to run any toast coming across old system and to save researching the usage of
  //  the service now. In the future you can run a find replace on the this service and replace with bstoastservice
  //  which is imported here for now - just so we can sort jump between the two service temporarily as we migrate to bestoastservice

  success(message) {
    this.bsSuccess(message);
    this.configuration.closeButton = false;
    this.configuration.timeOut = this.timeout;
    this.configuration.closeTime = this.close;
    // this.aToastr.success(message, 'Success', this.configuration);
  }

  bsSuccess(message: string) {
    this.bsToast.showSuccessToast(message, 'Success');
  }

  error(message) {
    // toss the undefineds - will surface when throwing other http errors the interceptor picks off
    if (message.toUpperCase().includes('UNDEFINED')) {
      return;
    } else {
      this.configuration.closeButton = false;
      this.configuration.timeOut = this.timeout;
      this.configuration.closeTime = this.close;
      this.bsError(message);
      // console.log(message);
      // this.aToastr.error(message, 'Error', this.configuration);
    }
  }

  bsError(message: string) {
    // console.log(message);
    this.bsToast.showErrorToast(message, 'Error' );
  }

  errorStayOnScreen(message) {
    this.configuration.closeButton = true;
    this.configuration.extendedTimeOut = 0;
    this.configuration.timeOut = 0;
    this.configuration.tapToDismiss = true;
    // console.log(message);
    this.bsError(message);
    // this.aToastr.error(message, 'Error' , this.configuration);
  }

  warning(message) {
    this.configuration.closeButton = false;
    this.configuration.timeOut = this.timeout;
    this.configuration.closeTime = this.close;
    // this.aToastr.warning(message, 'Warning', this.configuration);
    this.bsWarning(message);
  }

  bsWarning(message: string) {
    this.bsToast.showWarningToast(message, 'Warning' );
  }

  warningStayOnScreen(message) {
    this.configuration.closeButton = true;
    this.configuration.extendedTimeOut = 0;
    this.configuration.timeOut = 0;
    this.configuration.tapToDismiss = false;
    this.bsWarning(message);
    // this.aToastr.warning(message, 'Warning', this.configuration);
  }

  info(message) {
    this.configuration.closeButton = false;
    this.configuration.timeOut = this.timeout;
    this.configuration.closeTime = this.close;
    // this.aToastr.info(message, 'Info', this.configuration);
    this.bsInfo(message);
  }

  bsInfo(message: string) {
    this.bsToast.showInfoToast(message, 'Info');
  }
}
